/**
 * `console.warn`, but at most once per key for the lifetime of the page.
 *
 * The renderer warns about things that are permanent properties of the config — an unknown
 * `component.type`, a widget type that has not been ported yet, a `slider` block nobody
 * implements, a `GroupWidget` nested past the depth cap. None of those can be fixed at
 * runtime, and all of them are evaluated during render.
 *
 * A display runs unattended for weeks (CLAUDE.md constraint 5), so a warning on a render
 * path is a slow leak: the console buffer fills with the same line and pushes out the MQTT
 * diagnostics that are the only way to debug a wall-mounted screen remotely. Warning once
 * per key keeps the signal and drops the repetition.
 *
 * The key is chosen by the caller and must identify the *cause*, not the occurrence — e.g.
 * `widget-type:FooWidget`, not the component id — so n widgets sharing one bad type produce
 * one line.
 */

const warned = new Set<string>();

export function warnOnce(key: string, ...args: unknown[]): void {
  if (warned.has(key)) return;
  warned.add(key);
  console.warn(...args);
}

/**
 * Tests only. Module-level state survives between tests in the same file, so a test that
 * asserts a warning has to clear what an earlier test already claimed. Production code never
 * resets — a display resets by reloading the page.
 */
export function resetWarnOnce(): void {
  warned.clear();
}
